import type { RPhoneNumber } from 'v2/types/api/settings'

/**
 * The account's sandbox (trial) DID.
 *
 * number_service marks it `description=TRIAL_DID`, but that field is not
 * projected into any console-facing response, so it cannot be read directly.
 * On a trial account it can still be identified structurally: the account
 * cannot buy numbers until KYC clears, so a lone rented number IS the trial DID.
 *
 * Deliberately returns undefined for anything else. Two or more numbers means
 * the account got one some other way and picking the first would be a guess -
 * and a wrong sandbox number is worse than an honest "not assigned yet", since
 * every step of the guided setup tells the user to dial it.
 *
 * Replace this with the real field once number_service projects it; the
 * signature stays the same.
 */
export const resolveTrialNumberRow = (
  numbers: RPhoneNumber[] | undefined,
  isTrial: boolean,
): RPhoneNumber | undefined => {
  if (!isTrial || !numbers || numbers.length !== 1) return undefined
  return numbers[0]?.number ? numbers[0] : undefined
}

/**
 * The trial DID's number. Callers that also need the row's routing fields
 * (`app_type`, `app_id`, `phlo_ids`) should use `resolveTrialNumberRow`, so the
 * "a lone rented number IS the trial DID" rule stays stated once.
 */
export const resolveTrialNumber = (
  numbers: RPhoneNumber[] | undefined,
  isTrial: boolean,
): string | undefined => resolveTrialNumberRow(numbers, isTrial)?.number || undefined
